"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { formatCategoryLabel } from "../lib/formatters";

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  category: string;
}

interface BlogPostsSectionProps {
  currentCategory?: string;
  showRelatedOnly?: boolean;
}

export default function BlogPostsSection({
  currentCategory,
  showRelatedOnly = false,
}: BlogPostsSectionProps) {
  const [allPosts, setAllPosts] = useState<BlogPost[]>([]);
  const [groupedPosts, setGroupedPosts] = useState<Record<string, BlogPost[]>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch("/api/blog/posts");
        if (response.ok) {
          const data = await response.json();
          let posts = data.posts;

          // Filter posts based on showRelatedOnly flag
          if (showRelatedOnly && currentCategory) {
            posts = posts.filter(
              (post: BlogPost) => post.category === currentCategory
            );
          }

          setAllPosts(posts);

          // Group posts by category
          const grouped = posts.reduce((acc: Record<string, BlogPost[]>, post: BlogPost) => {
            if (!acc[post.category]) {
              acc[post.category] = [];
            }
            acc[post.category].push(post);
            return acc;
          }, {});

          setGroupedPosts(grouped);
        }
      } catch (error) {
        console.error("Failed to fetch blog posts:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [currentCategory, showRelatedOnly]);

  if (loading || allPosts.length === 0) {
    return null;
  }

  const categoryOrder = Object.keys(groupedPosts).sort();

  return (
    <div className="bg-[var(--bg-primary)]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="bg-[var(--bg-secondary)] rounded-xl sm:rounded-2xl p-6 md:p-8 shadow-lg">
          <h2 className="text-2xl font-semibold text-[var(--text-primary)] mb-6">
            Explore More Posts
          </h2>
          <div className="space-y-6">
            {categoryOrder.map((category) => (
              <div key={category}>
                <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-3">
                  {formatCategoryLabel(category)}
                </h3>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-4">
                  {groupedPosts[category].map((post) => (
                    <li key={post.slug}>
                      <Link
                        href={`/blog/${post.category}/${post.slug}`}
                        className="text-[var(--brand-primary)] hover:text-[var(--brand-secondary)] underline underline-offset-4 decoration-1 hover:decoration-2 transition-all"
                      >
                        {post.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
